import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LineChart, Brain, Database, CheckCircle2, Globe, Users, Star } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const stats = [
  {
    icon: CheckCircle2,
    startValue: 0,
    endValue: 200,
    suffix: '+',
    label: 'Projects Delivered',
    description: 'Comprehensive solutions spanning AI/ML implementation, process automation, data engineering, and platform integration across multiple industries.'
  },
  {
    icon: Users,
    startValue: 0,
    endValue: 50,
    suffix: '+',
    label: 'Clients Served',
    description: 'From mid-sized companies to enterprise organizations, helping businesses achieve digital transformation goals with proven methodologies.'
  },
  {
    icon: Globe,
    startValue: 0,
    endValue: 15,
    suffix: '+',
    label: 'Countries',
    description: 'Global delivery capabilities with successful implementations across Europe, Asia-Pacific, Middle East, North America, and Caribbean regions.'
  },
  {
    icon: Star,
    startValue: 0,
    endValue: 99,
    suffix: '%',
    label: 'Client Satisfaction',
    description: 'Sustained partnerships and repeat engagements demonstrating consistent delivery excellence and long-term value creation.'
  }
];

const AnimatedStat = ({ startValue, endValue, suffix, duration = 2000 }) => {
  const [value, setValue] = useState(startValue);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      setValue(Math.floor(progress * (endValue - startValue) + startValue));
      
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [startValue, endValue, duration, isVisible]);

  return (
    <div ref={countRef} className="text-3xl font-bold text-white">
      {value}{suffix}
    </div>
  );
};

const caseStudies = [
  {
    title: 'Retail Analytics Transformation',
    description: 'How we helped a leading UK retail pharmacy network transform their operations through comprehensive Power Platform implementation.',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80',
    industry: 'Healthcare/Retail',
    metrics: [
      '95% reduction in manual workflows',
      '5 days to 1 hour reporting time',
      '20+ hours weekly time saved'
    ],
    link: '/case-studies/retail-analytics'
  },
  {
    title: 'Healthcare AI Risk Prediction',
    description: 'How we helped a regional health network implement AI-driven predictive analytics to identify high-risk cardiac patients 30 days in advance.',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80',
    industry: 'Healthcare/AI-ML',
    metrics: [
      '85% accuracy in heart disease risk forecasting',
      '20% reduction in cardiac readmissions',
      '70% decrease in manual chart reviews'
    ],
    link: '/case-studies/healthcare-ai'
  },
  {
    title: 'Healthcare Communication Platform',
    description: 'How we helped a multi-facility healthcare network eliminate communication breakdowns through real-time clinical intelligence and patient safety monitoring.',
    image: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80',
    industry: 'Healthcare/Communication',
    metrics: [
      '99.99% system uptime achieved',
      '78% reduction in patient transfer incidents',
      '94% accuracy in medical terminology extraction'
    ],
    link: '/case-studies/healthcare-communication'
  },
  {
    title: 'Insurance Statement Processing',
    description: 'How we helped a leading insurance brokerage firm automate the processing of hundreds of daily statements from multiple insurers through intelligent document processing.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80',
    industry: 'Financial Services/Insurance',
    metrics: [
      '95% reduction in manual data entry',
      '500+ daily statements automated',
      'Real-time CRM synchronization'
    ],
    link: '/case-studies/insurance-automation'
  },
  {
    title: 'Manufacturing AI Forecasting',
    description: 'How we helped a mid-sized manufacturer implement intelligent forecasting and real-time analytics to eliminate stock-outs and optimize inventory management.',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80',
    industry: 'Manufacturing/Supply Chain',
    metrics: [
      '88% improvement in forecast accuracy',
      '40% reduction in safety stock levels',
      'Real-time supply chain visibility'
    ],
    link: '/case-studies/manufacturing-ai'
  },
  {
    title: 'Payment Processing Automation',
    description: 'How we helped a leading food delivery platform streamline weekly payment cycles for 1000+ delivery partners through intelligent automation and document processing.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80',
    industry: 'Food Delivery/Logistics',
    metrics: [
      '100% automation of payment processing',
      '3 days to 30 minutes processing time',
      'Eliminated Excel dependency completely'
    ],
    link: '/case-studies/payment-automation'
  },
  {
    title: 'Pharmaceutical Marketing Analytics Platform',
    description: 'How we automated physician-level data validation and multi-client reporting for a pharmaceutical marketing firm managing 15+ clients through a scalable Snowflake platform.',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80',
    industry: 'Pharmaceutical Marketing',
    metrics: [
      '100% automation across 15+ pharmaceutical clients',
      '95%+ data accuracy through NPI-based validation',
      '80% faster client onboarding'
    ],
    link: '/case-studies/pharma-analytics'
  },
  {
    title: 'Enterprise Database Migration & Cloud Data Platform',
    description: 'How we migrated a legacy enterprise database to Azure SQL with zero data loss and built a cloud-native AWS automation platform across 15+ enterprise services over 17 months.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80',
    industry: 'Technology & Consulting',
    metrics: [
      'Zero data loss across full Azure SQL migration',
      '15+ services automated on AWS',
      'Full Terraform IaC across all infrastructure'
    ],
    link: '/case-studies/cloud-platform-migration'
  },
  {
    title: 'AI-Powered Sports Prediction Platform',
    description: 'How we built a production-grade multi-sport ML prediction system across Soccer, NBA, and NASCAR with daily automated pipelines and positive ROI from month one.',
    image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80',
    industry: 'Sports & Entertainment',
    metrics: [
      '65% prediction accuracy across three sports',
      '70K+ training matches across 10 leagues',
      'Positive ROI from first month of operation'
    ],
    link: '/case-studies/sports-prediction'
  },
  {
    title: 'AI-Powered HSN Classification & Product Categorization',
    description: 'How we processed 21 million international trade shipment records in under 60 minutes using a hybrid ML pipeline for HSN code classification at under $50 per million records.',
    image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&q=80',
    industry: 'Global Trade',
    metrics: [
      '85%+ extraction accuracy (up from 40% baseline)',
      '21M records processed in under 60 minutes',
      '<$50 per million records, fully offline'
    ],
    link: '/case-studies/hsn-classification'
  },
  {
    title: 'FMCG Enterprise Azure Cloud Migration',
    description: 'How we migrated 200+ stored procedures and 1,000+ SSRS reports from legacy on-premises FMCG infrastructure to Microsoft Azure in 20 weeks with zero data loss.',
    image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80',
    industry: 'FMCG',
    metrics: [
      '40% cost reduction post-migration',
      '98%+ report conversion success rate',
      '99.9% uptime maintained post-cutover'
    ],
    link: '/case-studies/fmcg-migration'
  },
  {
    title: 'Business Process Automation Platform',
    description: 'How we eliminated duplicate client records, manual CSM assignments, and invoice blind spots through intelligent Slack-to-Smartsheet workflow automation for a 350+ client marketing agency.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80',
    industry: 'Marketing',
    metrics: [
      '100% duplicate elimination across all workflows',
      'Zero manual data entry in client onboarding',
      'Real-time CSM routing across 350+ accounts'
    ],
    link: '/case-studies/marketing-automation'
  },
  {
    title: 'Access Engineering Application Audit',
    description: 'How we delivered a comprehensive 8-week audit of 88 Ruby/Rails microservices processing 600–700K daily prior authorizations, surfacing HIPAA risks and reliability gaps.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80',
    industry: 'Healthcare',
    metrics: [
      '88 microservices fully audited and mapped',
      'Active PHI exposure surfaced and documented',
      'Full remediation roadmap delivered in 8 weeks'
    ],
    link: '/case-studies/healthcare-audit'
  },
  {
    title: 'AI-Native Ship Chandler RFQ Automation',
    description: 'How we eliminated 2–3 day manual quoting cycles for ship chandlers through AI-powered RFQ email parsing, IMPA/ISSA code matching, and margin-protected quote generation.',
    image: 'https://images.unsplash.com/photo-1519452575417-564c1401ecc0?auto=format&fit=crop&q=80',
    industry: 'Maritime',
    metrics: [
      '30-minute quote turnaround (from 2–3 days)',
      '$100K+ annual labor recovered per deployment',
      'Zero pricing errors since go-live'
    ],
    link: '/case-studies/ship-chandler-rfq'
  },
  {
    title: 'Unified Multi-Exchange Trading Automation',
    description: 'How we enabled 5,000+ active traders to execute automated strategies across 6 exchanges simultaneously with sub-500ms order execution and AES-256-GCM security.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80',
    industry: 'Trading & Investment',
    metrics: [
      '5,000+ active traders served simultaneously',
      '<500ms execution across 6 exchanges',
      '99.9% platform uptime with exponential backoff'
    ],
    link: '/case-studies/trading-automation'
  }
];

const CaseStudies = () => {
  return (
    <>
      <SEOHead
        title="Case Studies | Agility AI Success Stories"
        description="Real-world AI implementation success stories. See how we delivered results in healthcare, manufacturing, finance and more."
        url="https://www.agilitytech.ai/case-studies"
      />
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80"
            alt="Case Studies Banner"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Client Success Stories
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Explore how we've helped organizations across industries transform their operations 
              through innovative AI and data solutions. Over 200+ successful implementations across 
              50+ organizations, delivering measurable impact through cutting-edge technology solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Company Impact Statistics */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <stat.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div>
                    <AnimatedStat
                      startValue={stat.startValue}
                      endValue={stat.endValue}
                      suffix={stat.suffix}
                    />
                    <div className="text-teal-400 font-medium">{stat.label}</div>
                  </div>
                </div>
                <p className="text-gray-300">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12">
            {caseStudies.map((study, index) => (
              <Link
                key={index}
                to={study.link}
                className="group bg-gray-900/50 backdrop-blur-sm rounded-2xl overflow-hidden 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
              >
                <div className="md:flex">
                  <div className="md:w-2/5">
                    <div className="h-64 md:h-full relative">
                      <img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 
                          transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent" />
                    </div>
                  </div>
                  <div className="md:w-3/5 p-8">
                    <div className="text-sm text-teal-400 font-semibold mb-3">{study.industry}</div>
                    <h3 className="text-2xl font-bold text-white mb-4">{study.title}</h3>
                    <p className="text-gray-300 mb-6">{study.description}</p>
                    <div className="space-y-3 mb-6">
                      {study.metrics.map((metric, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-gray-400">
                          <ArrowRight className="h-4 w-4 text-teal-400" />
                          <span>{metric}</span>
                        </div>
                      ))}
                    </div>
                    <span className="inline-flex items-center text-teal-400 font-medium group-hover:text-teal-300">
                      Read Case Study
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
}

export default CaseStudies;